import React from 'react';
import styled from 'styled-components';
import { Level, Container, fontWeight, spacing } from '@landr/maestro';
import { useMatch } from 'react-router-dom';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { FooterNavigation, SocialNavigation } from 'components/Navigations';
import { RoutePaths } from 'types/enums';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import { useAuthentication } from '../AuthenticationProvider/AuthenticationProvider';
import {
    CreatorHeaderHeightPxDesktop,
    CreatorHeaderHeightPxMobile,
    CreatorBannerHeight,
} from '../../constants';
import Logo from './Logo';

const StyledFooter = styled.footer<{ isCreatorVisible: boolean }>`
    flex-shrink: 0;
    font-weight: ${fontWeight('bold')};
    padding: 100px 0 ${spacing(11)} 0;

    ${({ isCreatorVisible }): string => `
       & > div:first-child {
           margin-bottom: ${
               isCreatorVisible
                   ? `${CreatorHeaderHeightPxDesktop}px`
                   : `${CreatorBannerHeight}px`
           };
        }
    `};

    ${({ theme }): string => theme.breakpoint('lg')`
        padding: ${spacing(11)} ${spacing(6)};
    `};

    ${({ theme, isCreatorVisible }): string => theme.breakpoint('lg')`
        & > div:first-child {
            margin-bottom: ${
                isCreatorVisible
                    ? `${CreatorHeaderHeightPxMobile}px`
                    : `${CreatorBannerHeight}px`
            };
        }
    `};
`;

export const ResponsiveLevel = styled(Level)`
    min-height: 75px;
    ${({ theme }): string => theme.breakpoint('lg')`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    `}
`;

const Footer: React.FC = () => {
    const { isAuthenticated } = useAuthentication();

    const { isVisible: isCreatorVisible } = useCreatorContext();

    const toLocalizedPath = useToLocalizedPath();

    /**
     * Don't render the Footer when is a guest user on private routes.
     * This prevents flickering of the UI in between redirects to authentication services.
     */

    const isDownloadPath = useMatch(
        toLocalizedPath(`/${RoutePaths.Downloads}`),
    );
    const isFavoritesPath = useMatch(
        toLocalizedPath(`/${RoutePaths.Favorites}`),
    );
    const isCollectionsPath = useMatch(
        toLocalizedPath(`/${RoutePaths.Collections}`),
    );

    if (
        (isDownloadPath || isFavoritesPath || isCollectionsPath) &&
        !isAuthenticated
    ) {
        return null;
    }

    return (
        <StyledFooter data-testid="Footer" isCreatorVisible={isCreatorVisible}>
            <Container>
                <ResponsiveLevel align="center">
                    <Logo />
                    <FooterNavigation />
                    <SocialNavigation />
                </ResponsiveLevel>
            </Container>
        </StyledFooter>
    );
};

export default Footer;
